import { Component } from '@angular/core';
import { LoadingService } from '../../../services/loading.service';
import { MyTrainingsService } from '../../../services/my-trainings.service';
import { TrainingTypeModel } from '../../../models/training/training-type.model';
import { DxTabsTypes } from 'devextreme-angular/ui/tabs';
import { TranslateService } from '@ngx-translate/core';
import { PersonEventFlagService } from 'src/app/services/person-event-flag.service';
import { firstValueFrom, forkJoin, map, switchMap } from 'rxjs';
import { TableColumn } from 'src/app/models/table-column.model';
import { Router } from '@angular/router';
import CustomStore from 'devextreme/data/custom_store';
import { GetTrainingsRequest } from 'src/app/models/training/trainings-request.model';
import { EventTypeModel } from 'src/app/models/events/event-type.model';
import { PageStateService } from 'src/app/services/page-state.service';
import { PageStateFlags } from 'src/app/constants/page-state.constants';
import { getTrainingTypeLabel } from 'src/app/utils/training-type.utils';

@Component({
  selector: 'app-my-trainings',
  templateUrl: './my-trainings.component.html',
  styleUrls: ['./my-trainings.component.scss'],
  standalone: false
})
export class MyTrainingsComponent {
  tabs: any[] = [
    {
      id: 0,
      text: this.translateService.instant(
        'MY-TRAININGS.OVERVIEW.OPEN-TRAININGS'
      ),
    },
    {
      id: 1,
      text: this.translateService.instant(
        'MY-TRAININGS.OVERVIEW.COMPLETED-TRAININGS'
      ),
    },
  ];

  trainingColumns: TableColumn[] = [
    {
      caption: this.translateService.instant('COMMON.FIELDS.NAME'),
      dataField: 'name',
      cssClass: 'width-250',
      cellValue: (data: any): string => {
        return data.name;
      },
    },
    {
      caption: this.translateService.instant('COMMON.FIELDS.TRAINING-TYPE'),
      dataField: 'trainingType',
      cellValue: (data: any): string => {
        return getTrainingTypeLabel(data.trainingType, this.translateService)
      },
    },
    {
      caption: this.translateService.instant(
        'COMMON.FIELDS.TRAINING-CATEGORIES'
      ),
      dataField: 'trainingCategories',
      cssClass: 'width-250',
      headerFilter: (data: any) => {
        data.dataSource.postProcess = (results: any) => {
          const trainingCategoryNames = results.reduce(
            (a: any, b: any) => a.concat(b.value),
            []
          );
          const parsedTrainingCategoryNames: string[] = [];
          trainingCategoryNames.forEach((category: string) => {
            if (!category || !category.includes(', ')) {
              parsedTrainingCategoryNames.push(category);
            } else if (category.includes(', ')) {
              const categories = category.split(', ');
              parsedTrainingCategoryNames.push(...categories);
            }
          });
          const uniqueTrainingCategoryNames = [
            ...new Set(parsedTrainingCategoryNames),
          ];

          results.splice(0, results.length);
          uniqueTrainingCategoryNames.forEach((tcName) => {
            if (tcName) {
              results.push({
                text: tcName,
                value: tcName,
              });
            } else {
              results.push({
                text: '(Blanks)',
                value: null,
              });
            }
          });
          return results;
        };
      },
      filterExpression: (value: any) => {
        function getTrainingCategoriesFilterString(rowData: any) {
          const categories = rowData.trainingCategories.map(
            (tc: any) => tc.name
          );
          return !!categories.join(',') ? categories.join(',') : 'empty';
        }

        return [
          [getTrainingCategoriesFilterString, 'contains', value || 'empty'],
        ];
      },
      cellValue: (data: any | any): string => {
        return data.trainingCategories.map((tg: any) => tg.name).join(', ');
      },
    },
    {
      caption: this.translateService.instant('COMMON.FIELDS.TYPE'),
      dataField: 'type',
      cellValue: (data: any): string => {
        switch (data.type) {
          case 'Course':
            return this.translateService.instant(
              'COMMON.FIELDS.EVENT-TYPES.ELEARNING'
            );
          case 'Training':
            return this.translateService.instant('TRAINING.TITLE-VIEW');
          default:
            return 'n.a.';
        }
      },
    },
    {
      caption: '',
      dataField: 'this',
      cellType: 'action',
      disableFiltering: true,
      cellTemplate: 'actionsTemplate',
      actions: [
        {
          icon: 'link-tab',
          isShowAction: () => true,
          onClick: (training: any, e: any): void => {
            e.stopPropagation();
            e.preventDefault();
            e.stopImmediatePropagation();
            this.navigateToTrainingNewTab(training.id, training.type);
          },
        },
      ],
    },
  ];

  currentViewMode: 'widget' | 'list' = 'widget';
  availableViewModes: any[] = [
    { view: 'widget', icon: 'smalliconslayout' },
    { view: 'list', icon: 'menu' },
  ];

  selectedTab: number = 0;

  readonly pageStateKey = 'my-trainings-overview-page';
  readonly tableStateKey = 'my-trainings-overview-table';

  get isOpenTab() { 
    return this.selectedTab === 0; 
  }
  get isCompletedTab() { 
    return this.selectedTab === 1; 
  }

  trainingsCourses: any[] = [];
  openTrainingsData: any[] = [];

  completedTrainingsTotal: number = 0;
  completedTrainingsData: any[] = [];
  completedTrainingsDataSource: any;

  filters: MyTrainingsFilters = {
    trainingTypes: [],
    trainingCategories: [],
    eventTypes: [],
    searchString: '',
  };

  trainingTypes: any[] = [
    {
      id: TrainingTypeModel.IDD,
      name: this.translateService.instant('COMMON.FIELDS.TRAINING-TYPES.IDD'),
    },
    {
      id: TrainingTypeModel.Compliance,
      name: this.translateService.instant(
        'COMMON.FIELDS.TRAINING-TYPES.COMPLIANCE'
      ),
    },
    {
      id: TrainingTypeModel.General,
      name: this.translateService.instant(
        'COMMON.FIELDS.TRAINING-TYPES.GENERAL'
      ),
    },
  ];

  trainingCategories!: any[];

  eventTypes: any[] = [
    {
      id: 0,
      name: this.translateService.instant(
        'COMMON.FIELDS.EVENT-TYPES.IN-PERSON'
      ),
    },
    {
      id: 1,
      name: this.translateService.instant('COMMON.FIELDS.EVENT-TYPES.WEBINAR'),
    },
    {
      id: 2,
      name: this.translateService.instant(
        'COMMON.FIELDS.EVENT-TYPES.ELEARNING'
      ),
    },
  ];

  pageSize = 6;
  pageIndex = 0;

  constructor(
    private myTrainingsService: MyTrainingsService,
    private loaderService: LoadingService,
    private translateService: TranslateService,
    private personEventFlagService: PersonEventFlagService,
    private router: Router,
    private pageStateService: PageStateService
  ) {
  }

  ngOnInit(): void {
    if (!this.pageStateService.consumeFlag(
      PageStateFlags.RestoreMyTrainingsOverviewTable
    )) {
      this.pageStateService.clear(this.tableStateKey);
      this.pageStateService.clear(this.pageStateKey);
    }

    this.loadPageState();
    this.loadMyTrainings();
  }

  onTabChange(e: DxTabsTypes.ItemClickEvent) {
    this.filters.trainingTypes = [];
    this.filters.trainingCategories = [];
    this.filters.eventTypes = [];
    this.filters.searchString = '';

    this.selectedTab = e.itemData.id;
    this.filterMyTrainings();
  }

  onTabChangeMobile(e: any) {
    this.filters.trainingTypes = [];
    this.filters.trainingCategories = [];
    this.filters.eventTypes = [];
    this.filters.searchString = '';

    this.selectedTab = e.value.id;
    this.filterMyTrainings();
  }

  loadMyTrainings() {
    const completedObs = this.getCompletedTrainingsObservable(this.pageIndex, this.pageSize);

    const obs = forkJoin({
      trainings: this.myTrainingsService.getMyTrainings(),
      courses: this.myTrainingsService.getMyCourses(),
      personEventFlags: this.personEventFlagService.getPersonAllEventsFlags('Bookmark'),
      completed: completedObs
    });

    this.loaderService.load(obs)
      .subscribe(({ trainings, courses, personEventFlags, completed }) => {
        this.trainingsCourses = [
          ...trainings.map((training) => ({
            ...training,
            trainers: training.trainers,
            type: 'Training',
          })),
          ...courses.map((course) => ({
            ...course,
            imageUrl: course.customImageFile?.fileUrl ?? course.imageFile?.fileUrl,
            trainers: course.trainers,
            type: 'Course',
          })),
        ];

        this.trainingsCourses.forEach(event => {
          event.isBookmarked = personEventFlags.some(f => 
            f.eventId === event.id && 
            f.actionFlag === 1
          );
        });

        const openTrainings = this.getOpenTrainings();
        this.tabs[0].text = `${this.tabs[0].text} (${openTrainings.length})`;

        this.completedTrainingsData = completed.data.map(t => ({
          ...t,
          isNew: false,
          type: t.type == EventTypeModel.Normal ? 'Training' : 'Course'
        }));
        this.completedTrainingsTotal = completed.totalCount;
        this.tabs[1].text = `${this.tabs[1].text} (${completed.totalCount})`;

        this.openTrainingsData = this.getOpenTrainings();
        this.setTrainingCategories();

        this.initCompletedTrainingsDataSource();
        this.filterMyTrainings();
      });
  }

  initCompletedTrainingsDataSource() {
    this.completedTrainingsDataSource = new CustomStore({
      key: 'id',
      load: async (loadOptions: any) => {
        const pageIndex = loadOptions.take
          ? Math.floor(loadOptions.skip / loadOptions.take)
          : 0;
        const pageSize = loadOptions.take || this.pageSize;

        const obs = this.getCompletedTrainingsObservable(pageIndex, pageSize);
        const result = await firstValueFrom(obs);

        const data =  result.data.map(t => ({
          ...t,
          isNew: false,
          type: t.type == EventTypeModel.Normal ? 'Training' : 'Course'
        }));

        return {
          data: data,
          totalCount: result.totalCount
        };
      }
    });
  }

  loadCompletedTrainingsWidgetPage() {
    const obs = this.getCompletedTrainingsObservable(this.pageIndex, this.pageSize);

    this.loaderService
      .load(obs)
      .subscribe(result => {
        this.completedTrainingsTotal = result.totalCount;
        this.completedTrainingsData = result.data.map(t => ({
          ...t,
          isNew: false,
          type: t.type === EventTypeModel.Normal ? 'Training' : 'Course'
        }));
      });
  }

  private getCompletedTrainingsObservable(pageIndex: number, pageSize: number) {
    const request: GetTrainingsRequest = {
      pageIndex,
      pageSize,
      trainingCategories: this.filters.trainingCategories,
      search: this.filters.searchString,
      trainingTypes: this.filters.trainingTypes,
      eventTypes: this.filters.eventTypes,
    };

    return this.myTrainingsService.getMyCompletedTrainings(request);
  }


  onWidgetPageChange(event: any) {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;

    if (this.isCompletedTab) {
      this.loadCompletedTrainingsWidgetPage();
    }
  }


  getCurrentWidgetData(): any[] {
    return this.isOpenTab ? this.openTrainingsData : this.completedTrainingsData;
  }

  getCurrentTableData() {
    return this.isOpenTab ? this.openTrainingsData : this.completedTrainingsDataSource;
  }

  getCurrentPagingMode() {
    return this.isOpenTab ? 'client' : 'server';
  }

  getOpenTrainings() {
    const trainings = this.trainingsCourses;
    const currentDatetime = new Date();

    return trainings.filter((event: any) => {
      if (event.type !== 'Training') return true;

      const { dates } = event;
      if (!Array.isArray(dates) || dates.length === 0) return true;

      const lastDate = dates[dates.length - 1];
      if (!lastDate?.end) return true;

      return new Date(lastDate.end) >= currentDatetime;
    });
  }

  setTrainingCategories() {
    let trainingCategories: any[] = [];
    this.openTrainingsData.forEach((training: any) => {
      if (training.trainingCategories.length) {
        trainingCategories = [
          ...trainingCategories,
          ...training.trainingCategories,
        ];
      }
    });

    const seenIds = new Set();
    this.trainingCategories = trainingCategories.filter((category) => {
      if (seenIds.has(category.id)) {
        return false;
      } else {
        seenIds.add(category.id);
        return true;
      }
    });
  }

  filterByTrainingType() {
    this.openTrainingsData = this.openTrainingsData.filter((obj: any) => {
      if (
        !this.filters.trainingTypes.length ||
        this.filters.trainingTypes.includes(obj.trainingType)
      ) {
        return true;
      } else {
        return false;
      }
    });
  }

  filterByCategory() {
    if (this.filters.trainingCategories!.length) {
      this.openTrainingsData = this.openTrainingsData.filter((training: any) =>
        training.trainingCategories.some((category: any) =>
          this.filters.trainingCategories!.includes(category.id)
        )
      );
    }
  }

  filterByEventType() {
    if (this.filters.eventTypes.length) {
      let filteredTrainings: any = [];
      if (this.filters.eventTypes.includes(0)) {
        filteredTrainings = [
          ...filteredTrainings,
          ...this.openTrainingsData.filter((obj: any) => {
            return obj.location &&
              obj.location.hasOwnProperty('type') &&
              obj.location.type == 0
              ? true
              : false;
          }),
        ];
      }
      if (this.filters.eventTypes.includes(1)) {
        filteredTrainings = [
          ...filteredTrainings,
          ...this.openTrainingsData.filter((obj: any) => {
            return obj.location && obj.location.type && obj.location.type != 0
              ? true
              : false;
          }),
        ];
      }
      if (this.filters.eventTypes.includes(2)) {
        filteredTrainings = [
          ...filteredTrainings,
          ...(this.openTrainingsData = this.openTrainingsData.filter((obj: any) => {
            return !obj.location && !obj.date ? true : false;
          })),
        ];
      }

      this.openTrainingsData = filteredTrainings;
    }
  }

  filterMyTrainings() {
    if (this.isOpenTab) {
      this.openTrainingsData = this.getOpenTrainings();

      this.filterByTrainingType();
      this.filterByCategory();
      this.filterByEventType();

      if (!!this.filters.searchString) {
        this.openTrainingsData = this.openTrainingsData.filter((obj: any) => {
          return (
            obj.name
              .toLowerCase()
              .includes(this.filters.searchString.toLowerCase()) ||
            (obj.description || '')
              .toLowerCase()
              .includes(this.filters.searchString.toLowerCase())
          );
        });
      }

      this.openTrainingsData = this.openTrainingsData.sort((a: any, b: any) => {
        const start = a.dates ? new Date(a.dates[0].start).getTime() : null;
        const end = b.dates ? new Date(b.dates[0].start).getTime() : null;

        if (start && end) {
          return start - end;
        } else {
          return 0;
        }
      });
    } else {
      if(this.currentViewMode == 'widget') {
        this.pageIndex = 0;
        this.loadCompletedTrainingsWidgetPage();
      } else {
        this.initCompletedTrainingsDataSource();
      }
    }

    this.savePageState();
  }

  onSearchInput(event: any) {
    this.filters.searchString = event.value;
    this.filterMyTrainings();
  }

  onTrainingTypeSelected(e: any) {
    if (e.node.selected) {
      this.filters.trainingTypes = [
        ...new Set([...this.filters.trainingTypes, e.node.key])
      ];
    } else {
      this.filters.trainingTypes = this.filters.trainingTypes.filter(
        (id) => id !== e.node.key
      );
    }
    this.filterMyTrainings();
  }

  onTrainingCategorySelected(e: any) {
    if (e.node.selected) {
      if (!this.filters.trainingCategories!.includes(e.node.key)) {
        this.filters.trainingCategories = [
          ...this.filters.trainingCategories!,
          e.node.key,
        ];
      }
    } else {
      this.filters.trainingCategories = this.filters.trainingCategories!.filter(
        (id) => id !== e.node.key
      );
    }
    this.filterMyTrainings();
  }

  onEventTypeSelected(e: any) {
    if (e.node.selected) {
      if (!this.filters.eventTypes.includes(e.node.key)) {
        this.filters.eventTypes = [
          ...this.filters.eventTypes,
          e.node.key,
        ];
      }
    } else {
      this.filters.eventTypes = this.filters.eventTypes.filter(
        (id) => id !== e.node.key
      );
    }
    this.filterMyTrainings();
  }

  onViewModeChange(event: any) {
    this.currentViewMode = event.itemData.view;
    this.savePageState();
    
    if(this.isCompletedTab && this.currentViewMode === 'widget') {
      this.pageIndex = 0;
      this.loadCompletedTrainingsWidgetPage();
    }
  }

  navigateToTrainingNewTab(id: string, type: string) {
    let url;
    if (type === 'Training') {
      url = this.router.serializeUrl(
        this.router.createUrlTree(['my-trainings', 'training', id])
      );
    } else {
      url = this.router.serializeUrl(
        this.router.createUrlTree(['my-trainings', 'course', id])
      );
    }

    window.open(url, '_blank');
  }

  navigateToTraining(id?: string) {
    const source = this.isOpenTab 
     ? this.openTrainingsData 
     : this.completedTrainingsData;

    const training = source.find(t => t.id === id);
    if (!training) return;

    if (training.type === 'Training') {
      this.router.navigate(['my-trainings', 'training', id]);
    } else {
      this.router.navigate(['my-trainings', 'course', id]);
    }
  }


   private savePageState(): void {
    this.pageStateService.save(this.pageStateKey, {
      selectedTab: this.selectedTab,
      currentViewMode: this.currentViewMode,
      filters: this.filters,
      pageIndex: this.pageIndex
    });
  }
  
  private loadPageState(): void {
    const state = this.pageStateService.load<{
      currentViewMode: 'widget' | 'list'
      selectedTab: number,
      pageIndex: number,
      filters: {
        trainingTypes: number[];
        trainingCategories?: string[];
        eventTypes: number[];
        searchString: string;
      }
    }>(this.pageStateKey);

    if (!state) return;

    this.selectedTab = state.selectedTab ?? 0;
    this.currentViewMode = state.currentViewMode ?? 'widget';
    this.pageIndex = state.pageIndex ?? 0;

    this.filters = {
      ...this.filters,
      ...state.filters
    };
  }

  syncTreeSelection(e: any, selectedIds?: any[] | null): void {
    if (!selectedIds?.length) {
      return;
    }

    e.component.unselectAll();

    selectedIds.forEach(id => e.component.selectItem(id));
  }
}

export interface MyTrainingsFilters {
  trainingTypes: number[];
  trainingCategories?: string[];
  eventTypes: number[];
  searchString: string;
}
